import {
  AccessLevel,
  Avatar,
  Booking,
  CustomValue,
  Importance,
  MatchedCustomValue,
  MatchReason,
  MatchScores,
  PermissionsGroup,
  Position,
  ProfileCustomValue
} from "PFTypes";

import { Id } from "./id";
import { ProfileSearchHighlights } from "./search_highlights";
import { Snakify } from "./snakify";

export type CurrentProfilePreferences = {
  officeWork?: boolean;
  remoteWork?: boolean;
  hybridWork?: boolean;
  projectMinDurationMonths?: number;
  projectMaxDurationMonths?: number;
  skills?: Id[];
};

export type ProfileCalendar = {
  calendarName?: string;
  calendarId: number;
  exceptionDays: object[];
  nominalDays: object[];
  startDate: string;
};

import { FeatureFlag } from "./feature_flag";

export type ContactInformationType =
  | "email"
  | "phone"
  | "ms_teams"
  | FeatureFlag.Facebook
  | FeatureFlag.Twitter
  | FeatureFlag.Instagram
  | FeatureFlag.Website;

export type ContactInformation = {
  type: ContactInformationType;
  value: string;
  label?: string;
  url?: string;
};

export type KnownProfileCustomFields = {
  skills?: ProfileCustomValue[];
  serviceLine?: CustomValue[];
  sectors?: CustomValue[];
  talentPool: CustomValue[];
  location?: CustomValue[];
  company?: CustomValue[];
  grade?: CustomValue[];
  department?: CustomValue[];
  competency?: CustomValue[];
  companyCode?: CustomValue[];
  costCentre?: CustomValue[];
  economicLocation?: CustomValue[];
  gpn?: CustomValue[];
  gui?: CustomValue[];
  hrDepartment?: CustomValue[];
  jobTitle?: CustomValue[];
  level?: CustomValue[];
  region?: CustomValue[];
  managementRegion?: CustomValue[];
  projectRoles?: CustomValue[];
  subArea?: CustomValue[];
  subCompetencyArea?: CustomValue[];
  subServiceLine?: CustomValue[];
  workLocationAddressCountry?: CustomValue[];
};

export type Profile = {
  id: number;
  unbiased?: boolean;
  avatar: Avatar;
  contactInformation: ContactInformation[];
  firstName: string;
  lastName: string | null;
  email: string;
  status: "active" | "inactive";
  active?: boolean;
  statistics?: ProfileStatistics;
  permissionsGroup: PermissionsGroup;
  named?: boolean;
  summary: string | null;
  updatedAt: string;
  suspendedAt?: string | null;
  availability?: {
    bookings: Booking[];
    calendars: { [key: string]: ProfileCalendar };
    calendarSubscriptionUrl?: string;
  };
  positions: Position[];
  roles: string[];
  skillCategories: Array<[string, string, string, number]>;
  suggestions: Suggestions;
  viewable: boolean;
  supervisor?: any;
  interested?: boolean;
  sourceId?: unknown;
  locale?: string;
  placeholder?: boolean;

  // TODO: [PROF-5746] left for compatibility with ProfileMinimized
  suspended_at?: string | null;
  first_name?: string;
  last_name?: string | null;

  connected_to_linkedin?: boolean;
  linkedin_uid?: string | null;
} & KnownProfileCustomFields;

// RegistrationStatus tells us which step has been already completed
export enum RegistrationStatus {
  Empty = "",
  New = "new",
  Credentials = "credentials",
  TermsOfService = "terms_of_service",
  Details = "details",
  Active = "active"
}

export type CurrentProfile = Profile & {
  preferences: CurrentProfilePreferences;
  accountId: number;
  progressTips: { name: string; profileSection: string }[];
  agreeToTermsAndConditions: boolean;
  registrationStatus: RegistrationStatus;
};

export type ProfileMinimized = Pick<
  Profile,
  | "id"
  | "firstName"
  | "lastName"
  | "email"
  | "status"
  | "viewable"
  | "avatar"
  | "active"
  | "suspendedAt"
  | "suspended_at"
  | "first_name"
  | "last_name"
>;

type Suggestions = {
  skills: SuggestionSkill[];
};

export type SuggestionSkill = {
  global_id: string;
  id: Id;
  value: string;
  importance: Importance;
};

type ProfileStatistics = {
  inviteAcceptancePercentage?: number;
  messagesSentCount?: number;
  progressCompletion?: number;
  skillsCount?: number;
};

export type _BasicProfile = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  avatar: Avatar;
  jobTitle?: string;
  text?: string;
  suspendedAt?: string | null;
};

export type BasicProfile = Snakify<_BasicProfile>;

export type AdminProfile = Profile & {
  adminEmails: boolean;
  employeeStatus: string;
  hidden?: boolean;
  suspended?: boolean;
};

export type ProfileOptionalFields = {
  availableMinutes?: number;
  highlights?: ProfileSearchHighlights;
};

export type MatchedProfile = {
  id: Id;
  profile: Profile & {
    pills: { customTypeId: number; text: string }[];
  };
  scores: MatchScores;
  customValues: {
    direct: MatchedCustomValue[];
    indirect: MatchedCustomValue[];
    related: MatchedCustomValue[];
    child: MatchedCustomValue[];
    parent: MatchedCustomValue[];
  };
  matchedBy: MatchReason[];
  optionalFields?: ProfileOptionalFields;
};

type PermissionSetting = {
  accessLevel: AccessLevel;
  mandatory: boolean;
  name: string;
};

export type PermissionSettings = {
  defaultProfileFieldsPermissions: PermissionSetting[];
  defaultActivityFieldsPermissions: PermissionSetting[];
};

export type ExportProfile = Profile & { score: string | null };
